"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  ClipboardCheck,
  CreditCard,
  Globe2,
  Mail,
  MessageSquareText,
  Search,
  Send,
  Sparkles,
} from "lucide-react";
import { config } from "@/shared/utils/config";
import TrustBand from "@/shared/marketing/TrustBand";

const signalTools = [
  { icon: Globe2, label: "Website" },
  { icon: Mail, label: "Replies" },
  { icon: CalendarDays, label: "Calls" },
  { icon: MessageSquareText, label: "Notes" },
  { icon: BarChart3, label: "Analytics" },
  { icon: CreditCard, label: "Revenue" },
  { icon: Search, label: "Competitors" },
  { icon: Sparkles, label: "Social signal" },
];

const executionSteps = [
  {
    icon: CircleDot,
    title: "Lucien finds the signal",
    body: "Spots which buyer, pain, channel, or competitor pattern is worth acting on now.",
  },
  {
    icon: ClipboardCheck,
    title: "Lucien prepares the work",
    body: "Drafts the rewrite, outreach, follow-up, buyer test, or watchlist for review.",
  },
  {
    icon: CheckCircle2,
    title: "Lucien runs after approval",
    body: "Executes the approved move, tracks the result, and updates what should happen next.",
  },
];

const artifactRows = [
  {
    label: "Input",
    value: "Website, 8 replies, 3 calls, revenue notes",
  },
  {
    label: "Lucien prepared",
    value: "Buyer segment, message rewrite, outreach follow-up",
  },
  {
    label: "Approved action",
    value: "Run the budget-trigger follow-up with 12 leads",
  },
  {
    label: "Tracked result",
    value: "Replies update the next action",
  },
];

const proofItems = [
  "Every claim is tied to source signal before Lucien uses it.",
  "You approve the move; Lucien executes the approved work.",
  "Replies, usage, and revenue update the next action.",
];

const faqs = [
  {
    question: "Is Lucien software or coaching?",
    answer:
      "Software. Lucien watches the GTM signal you provide, prepares work, waits for approval, then executes approved moves and learns from the result.",
  },
  {
    question: "Does Lucien act without me?",
    answer:
      "No. The valuable work is proactive, but execution is approval-gated. You review the move before Lucien runs it.",
  },
  {
    question: "What do I need to start?",
    answer:
      "A site, product idea, prototype, early product, or notes from customer conversations. More connected signal makes Lucien sharper, but you can start with what you have.",
  },
];

function PrimaryCta({
  children,
  ctaId,
  className = "",
}: Readonly<{ children: ReactNode; ctaId: string; className?: string }>) {
  return (
    <Link
      href="/sign-up"
      className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-blue px-6 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(0,71,249,0.25)] ring-4 ring-transparent hero-cta-animated [animation:hero-cta-ring_1.8s_ease-in-out_infinite_alternate] active:brightness-90 ${className}`}
      data-cta={ctaId}
    >
      {children}
      <ArrowRight className="size-4" aria-hidden="true" />
    </Link>
  );
}

export default function MobileLanding() {
  return (
    <div className="lg:hidden">
      <section
        id="mobile-hero"
        data-analytics-section="hero"
        className="relative overflow-hidden px-4 pb-5 pt-8"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[34rem] bg-[radial-gradient(circle_at_70%_0%,rgba(0,71,249,0.16),transparent_46%),linear-gradient(180deg,#f8fbff_0%,#ffffff_88%)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-16 -z-10 h-52 opacity-55 [background-image:linear-gradient(rgba(0,71,249,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(0,71,249,0.08)_1px,transparent_1px)] [background-size:42px_42px]"
        />

        <div className="mx-auto max-w-md">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue/15 bg-blue/5 px-3 py-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-blue">
            <Sparkles className="size-3.5" aria-hidden="true" />
            Always-on GTM execution
          </p>

          <h1 className="max-w-[10.5ch] font-sg text-[2.4rem] leading-[0.97] tracking-[-0.045em] text-slate-950 min-[390px]:text-[2.55rem]">
            Stop guessing. Let Lucien run the next GTM move.
          </h1>

          <p className="mt-4 text-[0.97rem] leading-6 text-slate-600">
            Connect your site, notes, replies, calls, analytics, and revenue. Lucien finds buyer
            signal, prepares the work, runs it after approval, and learns.
          </p>

          <div className="mt-5 space-y-3">
            <PrimaryCta ctaId="hero-get-started">Start 1-week free trial</PrimaryCta>
            <Link
              href="/pricing"
              className="inline-flex h-12 w-full items-center justify-center rounded-full border border-slate-300 bg-white/80 px-6 text-sm font-semibold text-slate-700 shadow-sm"
              data-cta="hero-see-pricing"
            >
              See pricing
            </Link>
            <p className="text-center text-xs font-medium text-slate-500">
              No credit card required. Plans from $39/mo.
            </p>
            <p className="mx-auto max-w-xs text-center text-xs font-medium leading-5 text-slate-600">
              Small validation cohort now open.
            </p>
          </div>

          <div className="mt-5 overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-950 p-4 text-white shadow-[0_22px_60px_rgba(15,23,42,0.18)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-white/45">
                  Buyer signal brief
                </p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight">Budget test ready</h2>
              </div>
              <span className="rounded-full border border-emerald-300/30 bg-emerald-300/12 px-3 py-1 text-xs font-semibold text-emerald-200">
                Drafted
              </span>
            </div>

            <div className="mt-5 space-y-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                <div className="flex items-center gap-3 text-white/55">
                  <Mail className="size-4" aria-hidden="true" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em]">Replies</span>
                </div>
                <p className="mt-2 text-base font-medium leading-6">
                  4 leads are curious, but budget urgency is still unproven.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <BarChart3 className="size-4 text-blue-200" aria-hidden="true" />
                  <p className="mt-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-white/45">
                    Usage
                  </p>
                  <p className="mt-1 text-sm font-medium leading-5">64% time on Copilot</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <MessageSquareText className="size-4 text-blue-200" aria-hidden="true" />
                  <p className="mt-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-white/45">
                    Calls
                  </p>
                  <p className="mt-1 text-sm font-medium leading-5">
                    2 buyers asked who owns handoff risk
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-blue/40 bg-blue/15 p-4">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-blue-100">
                  Action prepared
                </p>
                <p className="mt-2 text-base font-semibold leading-6">
                  I drafted follow-up to 8 leads to test whether handoff pain is urgent enough to
                  pay for now.
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    className="h-10 flex-1 rounded-full border border-amber-300/45 px-4 text-sm font-semibold text-amber-100"
                  >
                    Review
                  </button>
                  <button
                    type="button"
                    className="h-10 flex-1 rounded-full bg-emerald-300 px-4 text-sm font-semibold text-emerald-950"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="mobile-outcomes"
        data-analytics-section="deliverables"
        data-scroll-reveal
        className="landing-reveal px-4 py-8"
      >
        <div className="mx-auto max-w-md">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-blue">
            What Lucien does
          </p>
          <h2 className="mt-3 font-sg text-[2.05rem] leading-[1.02] tracking-[-0.035em] text-slate-950">
            Signal turns into work Lucien can run.
          </h2>
          <div className="mt-5 space-y-3">
            {executionSteps.map(function renderStep(step, index) {
              const Icon = step.icon;
              return (
                <article
                  key={step.title}
                  className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-[0_12px_36px_rgba(15,23,42,0.05)]"
                >
                  <div className="flex gap-4">
                    <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue/10 text-blue">
                      <Icon className="size-5" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        0{index + 1}
                      </div>
                      <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-950">
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{step.body}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        id="mobile-work"
        data-analytics-section="sprint"
        data-scroll-reveal
        className="landing-reveal px-4 py-8"
      >
        <div className="mx-auto max-w-md rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 shadow-[0_14px_44px_rgba(15,23,42,0.05)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-blue">
                Example artifact
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                What week one can produce.
              </h2>
            </div>
            <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-amber-700">
              Ready
            </span>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            A realistic example artifact, not fake customer proof.
          </p>
          <div className="mt-5 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {artifactRows.map(function renderRow(row) {
              return (
                <div key={row.label} className="p-4">
                  <p className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {row.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-slate-900">{row.value}</p>
                </div>
              );
            })}
          </div>
          <PrimaryCta ctaId="example-artifact-start-free" className="mt-5">
            Start free
          </PrimaryCta>
        </div>
      </section>

      <section
        id="mobile-integrations"
        data-analytics-section="integrations"
        data-scroll-reveal
        className="landing-reveal px-4 py-8"
      >
        <div className="mx-auto max-w-md">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-blue">
            Signal sources
          </p>
          <h2 className="mt-3 font-sg text-[2rem] leading-[1.02] tracking-[-0.035em] text-slate-950">
            Start with what you have.
          </h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Lucien gets sharper as you connect more context, but the first run can start from a
            site, notes, replies, or calls.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            {signalTools.map(function renderTool(tool) {
              const Icon = tool.icon;
              return (
                <div
                  key={tool.label}
                  className="inline-flex min-w-0 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm"
                >
                  <Icon className="size-4 text-blue" aria-hidden="true" />
                  <span className="truncate">{tool.label}</span>
                </div>
              );
            })}
          </div>
          <TrustBand className="-mx-4 mt-5" compact showIntro={false} footerText="...and more." />
        </div>
      </section>

      <section
        id="mobile-proof"
        data-analytics-section="proof"
        data-scroll-reveal
        className="landing-reveal px-4 py-8"
      >
        <div className="mx-auto max-w-md">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-blue">
            Why it matters
          </p>
          <h2 className="mt-3 font-sg text-[2rem] leading-[1.02] tracking-[-0.035em] text-slate-950">
            Building is not the bottleneck. Market signal is.
          </h2>
          <div className="mt-5 overflow-hidden rounded-[1.45rem] border border-slate-200 bg-white shadow-[0_14px_44px_rgba(15,23,42,0.05)]">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Lucien trust rule
              </p>
              <p className="mt-1 text-base font-semibold leading-6 text-slate-950">
                No unsupported GTM advice.
              </p>
            </div>
            <div className="divide-y divide-slate-200">
              {proofItems.map(function renderProofItem(item) {
                return (
                  <div key={item} className="flex gap-3 p-4">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-blue" aria-hidden="true" />
                    <p className="text-sm font-medium leading-6 text-slate-700">{item}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section
        id="mobile-faq"
        data-analytics-section="faq"
        data-scroll-reveal
        className="landing-reveal px-4 py-8"
      >
        <div className="mx-auto max-w-md">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-blue">
            Questions
          </p>
          <h2 className="mt-3 font-sg text-[2rem] leading-[1.02] tracking-[-0.035em] text-slate-950">
            The operating rules are simple.
          </h2>
          <div className="mt-5 divide-y divide-slate-200 overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white">
            {faqs.map(function renderFaq(faq) {
              return (
                <details
                  key={faq.question}
                  className="group p-4"
                  data-analytics-faq-question={faq.question}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-slate-950">
                    {faq.question}
                    <span className="text-blue transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{faq.answer}</p>
                </details>
              );
            })}
          </div>
        </div>
      </section>

      <section
        data-analytics-section="final_cta"
        data-scroll-reveal
        className="landing-reveal px-4 py-10"
      >
        <div className="mx-auto max-w-md rounded-[1.8rem] bg-blue p-6 text-white shadow-[0_24px_64px_rgba(0,71,249,0.26)]">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-white/65">
            Start the loop
          </p>
          <h2 className="mt-3 font-sg text-[2rem] leading-[1.02] tracking-[-0.035em]">
            Give Lucien one messy signal.
          </h2>
          <p className="mt-3 text-sm leading-6 text-white/78">
            Lucien will prepare the first buyer signal brief and one action to approve.
          </p>
          <Link
            href="/sign-up"
            className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-semibold text-blue"
            data-cta="final-sign-up"
          >
            Start free
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
          <Link
            href={config.cal.url}
            target="_blank"
            className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-white/35 px-6 text-sm font-semibold text-white"
            data-cta="final-founder-chat"
          >
            Talk through my GTM
            <Send className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
